import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import { colors } from 'styles/theme/colors';

import { ReferralAnalyticsQueryParams } from './query/useReferralAnalytics';

export type ViewByOption = {
  label: string;
  value: ViewByValue;
  dimension: Dimensions;
};

export const VIEW_BY = {
  SENDING_GROUP: 'sendingGroup',
  RECEIVING_CLIENT: 'receivingClient',
  HEALTH_PLAN: 'healthPlan',
} as const;

export type ViewByValue = (typeof VIEW_BY)[keyof typeof VIEW_BY];

export const STATUS_COLORS: Record<string, string> = {
  accepted: colors.accentGreen,
  rejected: colors.accentRed,
  canceled: colors.black50,
  pending: colors.black10,
};

export const REFERRAL_STATUS_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  rejected: 'Denied',
  canceled: 'Canceled',
  pending: 'Pending',
};

export const REFERRAL_STATUSES = Object.keys(REFERRAL_STATUS_LABELS);

export const VIEW_BY_DIMENSION_MAP: Record<ViewByValue, Dimensions> = {
  sendingGroup: Dimensions.SENDING_GROUP,
  receivingClient: Dimensions.RECEIVING_CLIENT,
  healthPlan: Dimensions.HEALTH_PLAN_CLASSIFICATION,
};

const SENDING_DIMENSIONS = new Set([Dimensions.SENDING_GROUP]);
const RECEIVING_DIMENSIONS = new Set([Dimensions.RECEIVING_CLIENT, Dimensions.RECEIVING_GROUP]);
const PLAN_DIMENSIONS = new Set([Dimensions.HEALTH_PLAN_CLASSIFICATION, Dimensions.PLAN_TYPE_CLASSIFICATION]);

export const getReferralCountLabel = (dimension: Dimensions): string => {
  if (SENDING_DIMENSIONS.has(dimension)) return 'Referrals Sent';
  if (RECEIVING_DIMENSIONS.has(dimension)) return 'Referrals Received';
  if (PLAN_DIMENSIONS.has(dimension)) return 'Total Referrals';
  return 'Referrals Sent';
};

export const getEntityColumnHeader = (
  dimension: Dimensions,
  {
    sendingGroupTypeName,
    receivingGroupTypeName,
  }: { sendingGroupTypeName?: string; receivingGroupTypeName?: string } = {}
): string => {
  switch (dimension) {
    case Dimensions.SENDING_GROUP:
      return `Referring ${sendingGroupTypeName ?? ''}`.trim();
    case Dimensions.RECEIVING_CLIENT:
      return `${receivingGroupTypeName ?? ''} Company`.trim();
    case Dimensions.RECEIVING_GROUP:
      return receivingGroupTypeName ?? '';
    case Dimensions.HEALTH_PLAN_CLASSIFICATION:
      return 'Plan';
    case Dimensions.PLAN_TYPE_CLASSIFICATION:
      return 'Plan Type';
    default:
      return '';
  }
};

export const formatResponseTime = (seconds: number | null | undefined): string => {
  if (seconds == null || isNaN(seconds)) return '—';
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0 && minutes === 0) return '<0h 1m';
  return `${hours}h ${minutes}m`;
};

export const formatDaysValue = (days: number | null | undefined): string => {
  if (days == null || isNaN(days)) return '—';
  return `${days.toFixed(1)} days`;
};

export const formatPercentage = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
};

export const validMetricValue = (value: number | null | undefined): number | null =>
  value != null && value >= 0 ? value : null;

// For a sub-divisible dimension, the dimension that represents its sub-division. Used both for the
// caret-expand sub-rows in the slideout's table and for adding the sub-dim to the analytics query.
export const SUB_DIVISION_DIMENSION: Partial<Record<Dimensions, Dimensions>> = {
  [Dimensions.HEALTH_PLAN_CLASSIFICATION]: Dimensions.PLAN_TYPE_CLASSIFICATION,
  [Dimensions.RECEIVING_CLIENT]: Dimensions.RECEIVING_GROUP,
};

// The three dashboard-level view-by dimensions, in display order.
const DASHBOARD_DIMENSIONS: Dimensions[] = Object.values(VIEW_BY_DIMENSION_MAP);

// Dimensions that represent a discrete entity and therefore want `:with_id` appended so the
// analytics response carries IDs alongside names.
const ENTITY_DIMENSIONS = new Set<Dimensions>([...DASHBOARD_DIMENSIONS, ...Object.values(SUB_DIVISION_DIMENSION)]);

// View-by options the slideout offers for a clicked dimension: sub-dimension first (if any),
// then the remaining dashboard dimensions. First entry is the default selection.
export const getSlideoutViewByOptions = (clicked: Dimensions): Dimensions[] => {
  const subDim = SUB_DIVISION_DIMENSION[clicked];
  const others = DASHBOARD_DIMENSIONS.filter((d) => d !== clicked);
  return subDim ? [subDim, ...others] : others;
};

export const getDefaultSlideoutDimension = (clicked: Dimensions): Dimensions =>
  getSlideoutViewByOptions(clicked)[0] ?? clicked;

// Maps a clicked dimension to the analytics filter key that locks results to that entity.
export const DIMENSION_FILTER_KEY: Partial<Record<Dimensions, string>> = {
  [Dimensions.SENDING_GROUP]: 'sendingGroup',
  [Dimensions.RECEIVING_CLIENT]: 'receivingClient',
  [Dimensions.RECEIVING_GROUP]: 'receivingGroup',
  [Dimensions.HEALTH_PLAN_CLASSIFICATION]: 'healthPlanClassification',
  [Dimensions.PLAN_TYPE_CLASSIFICATION]: 'planTypeClassification',
};

// Dimensions that are themselves sub-divisible — clicking a row of this dimension can drill further.
// Used to decide whether to show a caret next to entity names in the slideout's table.
export const SUB_DIVISIBLE_DIMENSIONS = new Set<Dimensions>(Object.keys(SUB_DIVISION_DIMENSION) as Dimensions[]);

const REFERRAL_METRICS = [
  Metrics.REVIEW_ID_COUNT,
  Metrics.LOCATION_EPISODE_ID_COUNT,
  Metrics.ACCEPTANCE_RATE,
  Metrics.DENIAL_RATE,
  Metrics.CANCEL_RATE,
  Metrics.PENDING_RATE,
  Metrics.RESPONSE_TIME_SECONDS_AVERAGE,
  Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE,
];

export const getReferralDetailsParams = (dimension: Dimensions): ReferralAnalyticsQueryParams => ({
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [dimension, Dimensions.REFERRAL_STATUS],
  metrics: REFERRAL_METRICS,
  rollups: true,
});

export const getReferralExportParams = (dimension: Dimensions): ReferralAnalyticsQueryParams => ({
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [dimension],
  metrics: REFERRAL_METRICS,
});

// Single map of dimension → export filename slug, used by both dashboard and slideout exports.
export const DIMENSION_EXPORT_SLUGS: Partial<Record<Dimensions, string>> = {
  [Dimensions.SENDING_GROUP]: 'referring-group',
  [Dimensions.RECEIVING_CLIENT]: 'receiving-group',
  [Dimensions.RECEIVING_GROUP]: 'receiving-branch',
  [Dimensions.HEALTH_PLAN_CLASSIFICATION]: 'plan',
  [Dimensions.PLAN_TYPE_CLASSIFICATION]: 'plan-type',
};

export const getReferralExportFilename = (viewBy: ViewByValue): string =>
  `referral-coordination-details-by-${DIMENSION_EXPORT_SLUGS[VIEW_BY_DIMENSION_MAP[viewBy]]}`;

export const withDimensionIds = (params: ReferralAnalyticsQueryParams): ReferralAnalyticsQueryParams => ({
  ...params,
  dimensions: params.dimensions?.map((d) => (ENTITY_DIMENSIONS.has(d as Dimensions) ? `${d}:with_id` : d)),
});

// View-by chips for the metric-drill slideouts (response time / denial rate / acceptance rate).
// Same options for all three drills, in dashboard order.
export const METRIC_DRILL_VIEW_BY_OPTIONS: Dimensions[] = DASHBOARD_DIMENSIONS;
